import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsSalesSummaryData } from "src/features/Reports/types/report.types";
import { REPORTS_SALES_SUMMARY } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import AreaChartBar from "src/components/AreaChart/AreaChart";

const headerCellClass =
  "py-3 align-middle text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0] bg-[#f1f5f9] whitespace-nowrap sticky top-0 z-10";
const numHeaderClass = `${headerCellClass} text-right`;
const numCellClass = "py-2 px-2 text-right tabular-nums";

interface SalesSummaryReportPageProps {
  embedded?: boolean;
  input?: ReportQueryInput | null;
}

export default function SalesSummaryReportPage({ embedded, input: inputProp }: SalesSummaryReportPageProps = {}) {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const inputBuilt = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? { storeCorrelationId: hasRestaurant.correlationId, from: range.from, to: range.to }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const input = embedded ? inputProp ?? null : inputBuilt;

  const inputPrevious = useMemo((): ReportQueryInput | null => {
    if (!input?.from || !input?.to) return null;
    const from = new Date(input.from);
    const to = new Date(input.to);
    const diffMs = to.getTime() - from.getTime();
    const prevTo = new Date(from);
    prevTo.setDate(prevTo.getDate() - 1);
    prevTo.setHours(23, 59, 59, 999);
    const prevFrom = new Date(prevTo.getTime() - diffMs);
    prevFrom.setHours(0, 0, 0, 0);
    return { ...input, from: prevFrom.toISOString(), to: prevTo.toISOString() };
  }, [input]);

  const { data, loading, error } = useReportQuery<ReportsSalesSummaryData>(
    REPORTS_SALES_SUMMARY,
    "reportsSalesSummary",
    input
  );
  const { data: dataPrevious } = useReportQuery<ReportsSalesSummaryData>(
    REPORTS_SALES_SUMMARY,
    "reportsSalesSummaryPrevious",
    inputPrevious
  );

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const rowsDesc = useMemo(
    () => [...rows].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")),
    [rows]
  );
  const chartData = useMemo(() => {
    return [...rows]
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
      .map((row) => {
        const dateStr = row.date ?? "";
        const time = dateStr ? new Date(dateStr + "T00:00:00").getTime() : 0;
        return {
          name: dateStr,
          time,
          Net: row.netSalesCents != null ? row.netSalesCents / 100 : 0,
        };
      });
  }, [rows]);

  const totalTaxCents = useMemo(
    () => rows.reduce((sum, r) => sum + (r.taxCents ?? 0), 0),
    [rows]
  );
  const totalTipsCents = useMemo(
    () => rows.reduce((sum, r) => sum + (r.tipsCents ?? 0), 0),
    [rows]
  );
  const totalOrders = data?.totalOrders ?? 0;
  const totalNetCents = data?.totalNetCents ?? 0;
  const avgTicketCents = totalOrders > 0 ? Math.round(totalNetCents / totalOrders) : 0;

  const prevGross = dataPrevious?.totalGrossCents ?? 0;
  const prevNet = dataPrevious?.totalNetCents ?? 0;
  const prevOrders = dataPrevious?.totalOrders ?? 0;
  const trendGross =
    prevGross > 0 && data?.totalGrossCents != null
      ? Math.round(((data.totalGrossCents - prevGross) / prevGross) * 100)
      : null;
  const trendNet =
    prevNet > 0 && totalNetCents != null ? Math.round(((totalNetCents - prevNet) / prevNet) * 100) : null;
  const trendOrders =
    prevOrders != null && prevOrders > 0 && totalOrders != null
      ? Math.round(((totalOrders - prevOrders) / prevOrders) * 100)
      : null;

  const kpiItems = [
    {
      label: "Gross Sales",
      value: loading ? "—" : formatCentsToDollarsWithDollarSign(data?.totalGrossCents ?? 0),
      trend: trendGross,
    },
    { label: "Net Sales", value: loading ? "—" : formatCentsToDollarsWithDollarSign(totalNetCents), trend: trendNet },
    { label: "Orders", value: loading ? "—" : String(totalOrders), trend: trendOrders },
    { label: "Avg Ticket", value: loading ? "—" : formatCentsToDollarsWithDollarSign(avgTicketCents), trend: null },
    { label: "Tax", value: loading ? "—" : formatCentsToDollarsWithDollarSign(totalTaxCents), trend: null },
    { label: "Tips", value: loading ? "—" : formatCentsToDollarsWithDollarSign(totalTipsCents), trend: null },
  ];
  const kpiRow = (items: typeof kpiItems, rowKey: string) => (
    <div className="grid grid-cols-3">
      {items.map((item, colIndex) => (
        <div
          key={`${rowKey}-${item.label}`}
          className={`py-1.5 min-w-0 ${colIndex > 0 ? "border-l border-[#e5e7eb] pl-4" : "pr-4"}`}
        >
          <p className="text-[10px] uppercase tracking-wider text-[#9ca3af] mb-0.5">{item.label}</p>
          <p className="text-2xl font-semibold text-[#111827] tabular-nums">{item.value}</p>
          {item.trend != null && !loading && (
            <p
              className={`text-[11px] mt-0.5 font-medium tabular-nums ${
                item.trend >= 0 ? "text-[#059669]" : "text-[#dc2626]"
              }`}
            >
              {item.trend >= 0 ? "▲" : "▼"} {Math.abs(item.trend)}% vs previous period
            </p>
          )}
        </div>
      ))}
    </div>
  );

  const kpiStrip = (
    <div className="mb-6 rounded-xl border border-[#e5e7eb] bg-[#fafafa] px-5 py-5">
      {kpiRow(kpiItems.slice(0, 3), "r1")}
      <div className="mt-4">{kpiRow(kpiItems.slice(3, 6), "r2")}</div>
    </div>
  );

  const content = (
    <>
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <>
          {kpiStrip}
          {chartData.length > 0 && (
            <div className="rounded-[14px] bg-white p-4 border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)] mb-12 h-[320px] [@media(min-height:900px)]:h-[420px]">
              <AreaChartBar
                areaChartData={{
                  data: chartData,
                  graphKeys: [{ name: "Net", strokeColor: "#8fbfa8" }],
                }}
                fillOpacity={0.2}
                strokeWidth={1.25}
                timeDataKey="time"
              />
            </div>
          )}
          <div className="w-full bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-x-auto overflow-y-visible">
            <table className="w-full border-collapse table-auto">
                <thead>
                  <tr>
                    <th className={`pl-4 pr-2 ${headerCellClass}`}>Date</th>
                    <th className={`pr-4 pl-2 ${numHeaderClass}`}>Gross Sales</th>
                    <th className={`px-2 ${numHeaderClass}`}>Discounts</th>
                    <th className={`px-2 ${numHeaderClass}`}>Net Sales</th>
                    <th className={`px-2 ${numHeaderClass}`}>Tax</th>
                    <th className={`px-2 ${numHeaderClass}`}>Tips</th>
                    <th className={`px-2 ${numHeaderClass}`}>Orders</th>
                    <th className={`pr-4 pl-2 ${numHeaderClass}`}>Avg Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {rowsDesc.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-[#6b7280] text-sm">
                        No data for this period.
                      </td>
                    </tr>
                  ) : (
                    rowsDesc.map((row, i) => (
                      <tr
                        key={row.date ?? i}
                        className="border-t border-[#f1f5f9] text-sm text-[#111827] hover:bg-[#F9FAFB]"
                      >
                        <td className="py-2 pl-4 pr-2">{row.date ?? "—"}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.grossSalesCents ?? 0)}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.discountsCents ?? 0)}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.netSalesCents ?? 0)}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.taxCents ?? 0)}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.tipsCents ?? 0)}</td>
                        <td className={numCellClass}>{row.ordersCount ?? "—"}</td>
                        <td className={numCellClass}>{formatCentsToDollarsWithDollarSign(row.avgTicketCents ?? 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
        )}
        {content}
      </>
    );
  }
  return (
    <ReportPageLayout
      hideTitle
      datePreset={datePreset}
      onDatePresetChange={(preset) => setDatePreset(preset)}
      error={error}
    >
      {content}
    </ReportPageLayout>
  );
}
